// Shared finance chart kit — keeps every finance chart consistent: theme-aware
// tooltips with light/readable text (recharts' default label text is hard-coded
// dark and washes out on the dark chart surface), bar value labels, a fiscal-year
// range toggle, currency helpers, and the committee color ramp.

import { useMemo } from "react";

// ── number / currency helpers ────────────────────────────────────────────────
export function asNum(v: unknown, f = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : f;
  }
  return f;
}
export const usd0 = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
/** Compact currency for axes / labels: $62K, $1.2M. */
export function usdC(n: number): string {
  const a = Math.abs(n);
  if (a >= 1_000_000)
    return `${n < 0 ? "-" : ""}$${(a / 1_000_000).toFixed(2)}M`;
  if (a >= 1_000) return `${n < 0 ? "-" : ""}$${Math.round(a / 1_000)}K`;
  return usd0(n);
}

// ── shared recharts theming ──────────────────────────────────────────────────
/** Spread onto every <Tooltip/> so the label + items render in readable theme text. */
export const TOOLTIP_PROPS = {
  contentStyle: {
    background: "var(--surface-raised)",
    border: "1px solid var(--border-default)",
    borderRadius: 10,
    fontSize: 12,
    boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
    color: "var(--text-primary)",
  },
  labelStyle: {
    color: "var(--text-primary)",
    fontWeight: 600,
    marginBottom: 4,
  },
  itemStyle: { color: "var(--text-secondary)" },
  cursor: { fill: "var(--surface-overlay)", opacity: 0.4 },
} as const;

/** Color ramp for committee / categorical series. */
export const COLORS = [
  "#2a7a6c",
  "#3b82f6",
  "#8b5cf6",
  "#f59e0b",
  "#ef4444",
  "#10b981",
  "#ec4899",
  "#6366f1",
  "#14b8a6",
  "#a855f7",
];

export const ACCENT = "var(--color-dcvfd-accent)";
export const MUTED = "var(--text-muted)";
export const GRID = "var(--border-subtle)";

// ── bar value labels ─────────────────────────────────────────────────────────
// recharts passes geometry to a custom label renderer. These render compact
// currency in a soft grey just outside the bar end.

// recharts hands label renderers geometry whose numeric fields are typed
// loosely (string | number | undefined); coerce defensively.
type LabelVal = number | string | null | undefined;
type LabelProps = {
  x?: LabelVal;
  y?: LabelVal;
  width?: LabelVal;
  height?: LabelVal;
  value?: unknown;
};

/** Grey value label centered above a vertical bar. */
export function topLabel(props: LabelProps) {
  const x = asNum(props.x),
    y = asNum(props.y),
    width = asNum(props.width),
    v = asNum(props.value);
  if (!v) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      fontSize={11}
      fontWeight={600}
      fill="var(--text-muted)"
    >
      {usdC(v)}
    </text>
  );
}

/** Grey value label just right of a horizontal (vertical-layout) bar. */
export function endLabel(props: LabelProps) {
  const x = asNum(props.x),
    y = asNum(props.y),
    width = asNum(props.width),
    height = asNum(props.height),
    v = asNum(props.value);
  if (!v) return null;
  return (
    <text
      x={x + width + 6}
      y={y + height / 2}
      textAnchor="start"
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
      fill="var(--text-muted)"
    >
      {usdC(v)}
    </text>
  );
}

// ── fiscal-year range toggle ─────────────────────────────────────────────────
// A small pill toggle for choosing how wide a window of fiscal years to show.
// Pure presentational; the page filters its data on the returned count.

export type FyWindow = 3 | 5 | 7 | 0; // 0 = all

const FY_OPTIONS: { key: FyWindow; label: string }[] = [
  { key: 3, label: "3Y" },
  { key: 5, label: "5Y" },
  { key: 7, label: "7Y" },
  { key: 0, label: "All" },
];

export function FyRangeToggle({
  value,
  onChange,
  options = FY_OPTIONS,
}: {
  value: FyWindow;
  onChange: (w: FyWindow) => void;
  options?: { key: FyWindow; label: string }[];
}) {
  return (
    <div className="flex items-center gap-1">
      {options.map(({ key, label }) => {
        const active = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors min-h-[28px] ${
              active
                ? "bg-[#0E403A] text-white shadow-sm"
                : "bg-theme-overlay text-theme-muted hover:text-theme-primary"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/** Keep only the last `window` items of a fiscal-year-sorted array (0 = all). */
export function useFyWindow<T>(rows: T[], window: FyWindow): T[] {
  return useMemo(
    () => (window === 0 ? rows : rows.slice(-window)),
    [rows, window],
  );
}

// ── generic series toggle (e.g. Budget / Spend / Encumbrance) ─────────────────
export function SeriesToggle<T extends string>({
  options,
  active,
  onToggle,
}: {
  options: { key: T; label: string; color: string }[];
  active: Record<T, boolean>;
  onToggle: (k: T) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {options.map(({ key, label, color }) => {
        const on = active[key];
        return (
          <button
            key={key}
            type="button"
            onClick={() => onToggle(key)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors min-h-[28px] ${
              on
                ? "bg-theme-overlay text-theme-primary"
                : "bg-transparent text-theme-muted/60 hover:text-theme-muted"
            }`}
          >
            <span
              className="w-2.5 h-2.5 rounded-sm"
              style={{
                background: on ? color : "var(--text-muted)",
                opacity: on ? 1 : 0.4,
              }}
            />
            {label}
          </button>
        );
      })}
    </div>
  );
}
